package com.arena_web.ui.sections.cities

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.arena_web.data.dto.ClubDto
import com.arena_web.generated.GENERATED_CITIES
import com.arena_web.ui.components.StoreBadges
import com.arena_web.ui.components.navigation.NavAnchor
import com.arena_web.ui.components.widgets.Chip
import com.arena_web.ui.layouts.PageLayout
import com.arena_web.ui.sections.info.InfoPageScaffold
import com.arena_web.ui.sections.info.InfoParagraph
import com.arena_web.ui.sections.info.InfoSection
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.h1
import com.arena_web.ui.utils.isMobile
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.H1

data class CitySeo(
    val slug: String,
    val name: String,
    val country: String,
    val clubCount: Int,
    val sports: List<Pair<String, Int>>,
    val clubs: List<ClubDto>,
)

private fun sportsPhrase(sports: List<Pair<String, Int>>): String {
    val names = sports.map { it.first.lowercase() }
    return when {
        names.isEmpty() -> "a range of sports"
        names.size == 1 -> names[0]
        else -> names.dropLast(1).joinToString(", ") + " and " + names.last()
    }
}

private fun encodeURIComponent(s: String): String = js("encodeURIComponent(s)") as String

@Composable
fun CityLandingContent(city: CitySeo) {
    val ctx = rememberPageContext()
    PageLayout(
        seoTitle = "Find People to Play Sports in ${city.name} | Arena Pro Play",
        description = "Find sports players and clubs in ${city.name}. Discover ${city.clubCount} " +
            "venues for football, tennis, basketball, padel and volleyball, join local games and " +
            "meet players near you.",
        mobileReady = true,
    ) {
        CitySchema(city)
        // Hero
        H1(attrs = { style { property("display", "contents") } }) {
            SpanText(
                "Find People to Play Sports in ${city.name}",
                modifier = Modifier.h1().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        SpanText(
            "${city.clubCount} clubs across ${sportsPhrase(city.sports)}",
            modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
        // Sport chips
        Row(modifier = Modifier.styleModifier { property("gap", "8px"); property("flex-wrap", "wrap") }) {
            city.sports.forEach { (sport, count) -> Chip("$sport $count") }
        }
        // CTA to Arena map
        NavAnchor(
            "/arena?city=${encodeURIComponent(city.name)}",
            onNavigate = { ctx.router.navigateTo("/arena?city=${encodeURIComponent(city.name)}") }
        ) {
            SpanText(
                "Open ${city.name} on the map →",
                modifier = Modifier.bodyLRegular().color(AppColors.Primary)
            )
        }
        // Arena club grid (baked, crawlable club names)
        if (isMobile()) {
            com.arena_web.ui.sections.arena.mobile.MobileClubGrid(
                clubs = city.clubs,
                isLoadingMore = false,
                onLoadMore = {},
                selectedCity = city.name,
                sportName = "all",
                onClubClick = { ctx.router.navigateTo("/clubs/${it.id}") },
            )
        } else {
            com.arena_web.pages.ClubGrid(
                clubs = city.clubs,
                isLoadingMore = false,
                onLoadMore = {},
                selectedCity = city.name,
                sportName = "all",
                onClubClick = { ctx.router.navigateTo("/clubs/${it.id}") },
            )
        }
        // Get the app + explore
        StoreBadges()
        Row(modifier = Modifier.styleModifier { property("gap", "16px"); property("flex-wrap", "wrap") }) {
            NavAnchor("/cities", onNavigate = { ctx.router.navigateTo("/cities") }) {
                SpanText("All cities", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
            NavAnchor("/sports", onNavigate = { ctx.router.navigateTo("/sports") }) {
                SpanText("All sports", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
            NavAnchor("/about", onNavigate = { ctx.router.navigateTo("/about") }) {
                SpanText("What is Arena Pro Play?", modifier = Modifier.bodyLRegular().color(AppColors.Primary))
            }
        }
    }
}

/**
 * Injects a per-city CollectionPage + ItemList of SportsActivityLocation into
 * <head> at composition time so the static export snapshot captures it (the
 * composition-time DOM-mutation mechanism verified in Stream A). Real ratings
 * only; aggregateRating omitted when there are no reviews.
 */
@Composable
fun CitySchema(city: CitySeo) {
    remember(city.slug) {
        val items = city.clubs.mapIndexed { i, c ->
            val rating = if (c.totalRatingsCount > 0)
                ""","aggregateRating":{"@type":"AggregateRating","ratingValue":"${(kotlin.math.round(c.averageRating * 10) / 10.0)}","reviewCount":"${c.totalRatingsCount}"}"""
            else ""
            """{"@type":"ListItem","position":${i + 1},"item":{"@type":"SportsActivityLocation","name":${jsonStr(c.name)},"address":${jsonStr(c.address)},"url":"https://arenaproplay.com/clubs/${c.id}"$rating}}"""
        }.joinToString(",")
        val json = """{"@context":"https://schema.org","@type":"CollectionPage","name":${jsonStr("Sports clubs in ${city.name}")},"url":"https://arenaproplay.com/cities/${city.slug}","mainEntity":{"@type":"ItemList","numberOfItems":${city.clubs.size},"itemListElement":[$items]}}"""
        val head = document.head
        if (head != null) {
            val stale = head.querySelectorAll("script[data-city]")
            for (idx in stale.length - 1 downTo 0) { stale.item(idx)?.let { head.removeChild(it) } }
        }
        val el = document.createElement("script")
        el.setAttribute("type", "application/ld+json")
        el.setAttribute("data-city", city.slug)
        el.textContent = json
        head?.appendChild(el)
        true
    }
}

private fun jsonStr(s: String): String {
    val sb = StringBuilder("\"")
    for (ch in s) {
        when (ch) {
            '\\' -> sb.append("\\\\")
            '"' -> sb.append("\\\"")
            '\n' -> sb.append("\\n")
            '\r' -> sb.append("\\r")
            '\t' -> sb.append("\\t")
            // Kotlin/JS does not support JVM String.format; use padStart instead
            else -> if (ch < ' ') sb.append("\\u${ch.code.toString(16).padStart(4, '0')}") else sb.append(ch)
        }
    }
    return sb.append("\"").toString()
}

@Composable
fun countryName(code: String): String = COUNTRY_NAMES[code.uppercase()] ?: code.uppercase()

fun countryNamePlain(code: String): String = COUNTRY_NAMES[code.uppercase()] ?: code.uppercase()

private val COUNTRY_NAMES = mapOf(
    "ES" to "Spain", "UA" to "Ukraine", "SE" to "Sweden", "PL" to "Poland",
    "CZ" to "Czechia", "GB" to "United Kingdom", "NL" to "Netherlands",
    "DE" to "Germany", "AT" to "Austria", "HU" to "Hungary", "IT" to "Italy",
    "FR" to "France", "TR" to "Türkiye", "PT" to "Portugal", "BE" to "Belgium",
    "CH" to "Switzerland", "IE" to "Ireland", "DK" to "Denmark", "NO" to "Norway",
    "FI" to "Finland", "GR" to "Greece", "RO" to "Romania", "BG" to "Bulgaria",
    "HR" to "Croatia", "RS" to "Serbia", "SK" to "Slovakia", "SI" to "Slovenia",
    "LT" to "Lithuania", "LV" to "Latvia", "EE" to "Estonia",
)

@Composable
fun CitiesIndexContent() {
    val ctx = rememberPageContext()
    PageLayout(
        seoTitle = "Find Players & Sports Clubs by City | Arena Pro Play",
        description = "Find people to play sports with in your city. Browse sports clubs and players " +
            "across cities in Europe on Arena Pro Play.",
        mobileReady = true,
    ) {
        InfoPageScaffold(title = "Find Players & Clubs by City", titleIsH1 = true) {
            InfoParagraph(
                "Pick your city to find sports players near you, discover local clubs and venues, " +
                    "and join or create games."
            )
            GENERATED_CITIES.groupBy { it.country }
                .entries.sortedBy { countryNamePlain(it.key) }
                .forEach { (code, cities) ->
                    InfoSection(countryNamePlain(code)) {
                        cities.sortedBy { it.name }.forEach { city ->
                            NavAnchor("/cities/${city.slug}", onNavigate = { ctx.router.navigateTo("/cities/${city.slug}") }) {
                                InfoParagraph("${city.name} — ${city.clubCount} clubs")
                            }
                        }
                    }
                }
        }
    }
}
